import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, ShieldCheck, Users, Building2, HandHeart, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    overline: "Professional Training",
    title: "Trauma-Informed Training for Those Who Meet Survivors First.",
    subtitle: "The Pillar Project delivers survivor-centered, evidence-based trainings to law enforcement, first responders, healthcare staff, social-service organizations, and community groups. Trainings are trauma-informed and co-presented with survivor leaders when possible.",
    whoTitle: "Who We Train",
    audiences: ["Law-enforcement personnel","Police departments","First responders","Fire departments / Fire marshals","Hospital and healthcare personnel","Social-service organizations","Community groups","Businesses","Faith-based organizations","Other"],
    topicsTitle: "Training Topics",
    topics: ["Recognizing indicators of human trafficking","Responding to domestic violence","Understanding sexual-assault dynamics","Trauma-informed communication","Survivor-centered intervention","Responding safely without increasing danger","Referral and resource-navigation practices","Human trafficking during domestic calls","Barriers that prevent survivors from seeking help"],
    recentOverline: "Recent Training Delivered",
    recentText: "The Pillar Project has delivered human-trafficking identification training to members of the Baltimore County Police Department (Dundalk precinct), including scenario walkthroughs, a local prosecution case study, and trauma-informed response protocols.",
    recentNote: "Trainings are not part of a statewide certified curriculum. Content is designed to support agency policies and community response — not to replace mandatory certifications.",
    requestTitle: "Request a Training",
    requestText: "Tell us about your organization and audience. We'll follow up within a few business days to scope, schedule, and confirm delivery format.",
    submittedTitle: "Request Received",
    submittedText: "A member of The Pillar Project team will contact you shortly.",
    labels: {
      organization: "Organization", contact_name: "Contact Name", email: "Email", phone: "Phone",
      audience_type: "Audience Type", attendee_count: "Estimated Attendees", preferred_date: "Preferred Date / Timeframe",
      delivery_mode: "Delivery Mode", training_goals: "Training Goals", additional_comments: "Additional Comments",
      select: "Select…", in_person: "In-person", virtual: "Virtual", either: "Either", submit: "Request Training", submitting: "Submitting…",
    },
    toastSuccess: "Training request submitted",
    toastError: "Submission failed",
  },
  es: {
    overline: "Capacitación Profesional",
    title: "Capacitación Informada Sobre el Trauma para Quienes Encuentran Primero a los Sobrevivientes.",
    subtitle: "The Pillar Project ofrece capacitaciones centradas en el sobreviviente y basadas en evidencia para fuerzas del orden, primeros respondientes, personal de salud, organizaciones de servicios sociales y grupos comunitarios. Las capacitaciones están informadas sobre el trauma y se presentan junto a líderes sobrevivientes cuando es posible.",
    whoTitle: "A Quién Capacitamos",
    audiences: ["Personal de fuerzas del orden","Departamentos de policía","Primeros respondientes","Bomberos / Fiscales de incendios","Personal de hospitales y salud","Organizaciones de servicios sociales","Grupos comunitarios","Empresas","Organizaciones religiosas","Otro"],
    topicsTitle: "Temas de Capacitación",
    topics: ["Reconociendo indicadores de trata de personas","Respondiendo a la violencia doméstica","Entendiendo las dinámicas de la agresión sexual","Comunicación informada sobre el trauma","Intervención centrada en el sobreviviente","Responder de forma segura sin aumentar el peligro","Prácticas de referencia y navegación de recursos","Trata de personas durante llamadas domésticas","Barreras que impiden a los sobrevivientes buscar ayuda"],
    recentOverline: "Capacitación Reciente Impartida",
    recentText: "The Pillar Project ha impartido capacitación de identificación de trata de personas a miembros del Departamento de Policía del Condado de Baltimore (precinto Dundalk), incluyendo recorridos de escenarios, un estudio de caso de un procesamiento local y protocolos de respuesta informados sobre el trauma.",
    recentNote: "Las capacitaciones no son parte de un currículo estatal certificado. El contenido está diseñado para apoyar las políticas de las agencias y la respuesta comunitaria — no para reemplazar certificaciones obligatorias.",
    requestTitle: "Solicitar Capacitación",
    requestText: "Cuéntanos sobre tu organización y audiencia. Daremos seguimiento en unos días hábiles para definir alcance, agendar y confirmar el formato.",
    submittedTitle: "Solicitud Recibida",
    submittedText: "Un miembro del equipo de The Pillar Project se comunicará contigo pronto.",
    labels: {
      organization: "Organización", contact_name: "Nombre de Contacto", email: "Correo", phone: "Teléfono",
      audience_type: "Tipo de Audiencia", attendee_count: "Asistentes Estimados", preferred_date: "Fecha / Marco Preferido",
      delivery_mode: "Modo de Entrega", training_goals: "Objetivos de Capacitación", additional_comments: "Comentarios Adicionales",
      select: "Seleccionar…", in_person: "En persona", virtual: "Virtual", either: "Cualquiera", submit: "Solicitar Capacitación", submitting: "Enviando…",
    },
    toastSuccess: "Solicitud de capacitación enviada",
    toastError: "El envío falló",
  },
};

export default function TrainingPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "",
    audience_type: "", attendee_count: "", preferred_date: "",
    delivery_mode: "", training_goals: "", additional_comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, attendee_count: form.attendee_count ? parseInt(form.attendee_count, 10) : null };
      await axios.post(`${API}/training/request`, payload);
      toast.success(c.toastSuccess);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || c.toastError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="training-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="training-title">{c.title}</h1>
            <p className="text-lg text-warm-200 leading-relaxed max-w-2xl">{c.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-warm-700" />
              <h2 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.whoTitle}</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              {c.audiences.map((a) => (
                <li key={a} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" />{a}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-6 h-6 text-warm-700" />
              <h2 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.topicsTitle}</h2>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              {c.topics.map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-warm-500 mt-1.5 flex-shrink-0" />{t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-warm-50 border-y border-warm-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <Building2 className="w-7 h-7 text-warm-700 flex-shrink-0 mt-1" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-2">{c.recentOverline}</p>
              <p className="text-gray-800 leading-relaxed">{c.recentText}</p>
              <p className="text-xs text-gray-500 mt-3 italic">{c.recentNote}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" id="request">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-4">
              <HandHeart className="w-6 h-6 text-warm-700" />
              <h2 className="text-3xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.requestTitle}</h2>
            </div>
            <p className="text-gray-600 mb-8">{c.requestText}</p>
          </motion.div>

          {submitted ? (
            <div className="bg-warm-50 border border-warm-100 rounded-xl p-8 text-center" data-testid="training-submitted">
              <CheckCircle className="w-12 h-12 text-warm-700 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.submittedTitle}</h3>
              <p className="text-gray-600">{c.submittedText}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="training-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <TxtInput label={c.labels.organization} required value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testId="tr-org" />
                <TxtInput label={c.labels.contact_name} required value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testId="tr-name" />
                <TxtInput label={c.labels.email} type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="tr-email" />
                <TxtInput label={c.labels.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="tr-phone" />
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.audience_type}</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value })} data-testid="tr-audience">
                    <option value="">{c.labels.select}</option>
                    {c.audiences.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <TxtInput label={c.labels.attendee_count} type="number" value={form.attendee_count} onChange={(v) => setForm({ ...form, attendee_count: v })} testId="tr-count" />
                <TxtInput label={c.labels.preferred_date} value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testId="tr-date" />
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.delivery_mode}</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" value={form.delivery_mode} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} data-testid="tr-mode">
                    <option value="">{c.labels.select}</option>
                    <option value="in-person">{c.labels.in_person}</option>
                    <option value="virtual">{c.labels.virtual}</option>
                    <option value="either">{c.labels.either}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.training_goals}</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[100px]" value={form.training_goals} onChange={(e) => setForm({ ...form, training_goals: e.target.value })} data-testid="tr-goals" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.additional_comments}</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[80px]" value={form.additional_comments} onChange={(e) => setForm({ ...form, additional_comments: e.target.value })} data-testid="tr-comments" />
              </div>
              <Button type="submit" disabled={submitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="training-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {c.labels.submitting}</> : c.labels.submit}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function TxtInput({ label, value, onChange, required, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
